import React, { useState, useEffect, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import { getAffiliations, createAffiliation, createAffiliationsBulk, updateAffiliation } from '../services/adminService';
import {
    Alert,
    Box,
    Button,
    FormControl,
    InputLabel,
    MenuItem,
    Modal,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import ExcelColumnFilter from '../components/ExcelColumnFilter';

const operationTypeOptions = [
    { value: 't', label: 'Retail' },
    { value: 'r', label: 'Restaurante' },
    { value: 'h', label: 'Hotel' },
    { value: 'a', label: 'Renta de auto' }
];

const operationTypeLabelMap = operationTypeOptions.reduce((accumulator, option) => {
    accumulator[option.value] = option.label;
    return accumulator;
}, {});

const getOperationTypeLabel = (value) => operationTypeLabelMap[(value || '').toLowerCase()] || '-';

const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 500,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
    maxHeight: '90vh',
    overflow: 'auto'
};

const emptyAffiliationForm = {
    nombre: '',
    tipo_operacion: '',
    device_id: ''
};

const affiliationBulkColumns = ['nombre', 'tipo_operacion', 'device_id'];
const requiredAffiliationColumns = ['nombre', 'tipo_operacion', 'device_id'];

const Affiliations = () => {
    const [affiliations, setAffiliations] = useState([]);
    const [error, setError] = useState('');
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [creatingAffiliation, setCreatingAffiliation] = useState(false);
    const [editingAffiliationId, setEditingAffiliationId] = useState(null);
    const [affiliationForm, setAffiliationForm] = useState(emptyAffiliationForm);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [bulkResult, setBulkResult] = useState(null);
    const [columnFilters, setColumnFilters] = useState({});
    const fileInputRef = useRef(null);

    const fetchAffiliations = useCallback(async () => {
        try {
            const data = await getAffiliations();
            setAffiliations(data);
            setError('');
        } catch (err) {
            const detail = err?.response?.data?.message || err?.response?.data || err?.message;
            setError(`No se pudieron obtener las afiliaciones.${detail ? ` Detalle: ${detail}` : ''}`);
        }
    }, []);

    useEffect(() => {
        fetchAffiliations();
    }, [fetchAffiliations]);

    const handleCreateAffiliation = async (event) => {
        event.preventDefault();

        const normalizedDeviceId = affiliationForm.device_id.trim();
        if (!normalizedDeviceId) {
            setError('device_id es requerido.');
            return;
        }

        setCreatingAffiliation(true);
        setError('');

        try {
            const payload = {
                ...affiliationForm,
                device_id: normalizedDeviceId
            };

            if (editingAffiliationId) {
                await updateAffiliation(editingAffiliationId, payload);
            } else {
                await createAffiliation(payload);
            }

            setAffiliationForm(emptyAffiliationForm);
            setEditingAffiliationId(null);
            setShowCreateForm(false);
            fetchAffiliations();
        } catch (creationError) {
            const detail = creationError?.response?.data?.message || creationError?.response?.data || creationError?.message;
            setError(`No se pudo ${editingAffiliationId ? 'actualizar' : 'crear'} la afiliación.${detail ? ` Detalle: ${detail}` : ''}`);
        } finally {
            setCreatingAffiliation(false);
        }
    };

    const openCreateModal = () => {
        setAffiliationForm(emptyAffiliationForm);
        setEditingAffiliationId(null);
        setError('');
        setShowCreateForm(true);
    };

    const openEditModal = (affiliation) => {
        setAffiliationForm({
            nombre: affiliation.nombre || '',
            tipo_operacion: (affiliation.tipo_operacion || '').toLowerCase(),
            device_id: affiliation.device_id || ''
        });
        setEditingAffiliationId(affiliation.id);
        setError('');
        setShowCreateForm(true);
    };

    const closeModal = () => {
        setShowCreateForm(false);
        setEditingAffiliationId(null);
        setAffiliationForm(emptyAffiliationForm);
    };

    const updateColumnFilter = (column, values) => {
        setColumnFilters((prev) => ({
            ...prev,
            [column]: values
        }));
    };

    const clearFilters = () => setColumnFilters({});

    const getColumnValue = (affiliation, column) => {
        if (column === 'id') {
            return (affiliation.id ?? '').toString();
        }
        if (column === 'nombre') {
            return affiliation.nombre || '';
        }
        if (column === 'tipo_operacion') {
            return getOperationTypeLabel(affiliation.tipo_operacion);
        }
        if (column === 'device_id') {
            return affiliation.device_id || '';
        }
        return '';
    };

    const matchesColumnFilter = (value, selectedOptions) => {
        if (selectedOptions === undefined) {
            return true;
        }
        return selectedOptions.includes(value);
    };

    const filteredAffiliations = affiliations.filter((affiliation) => {
        return (
            matchesColumnFilter(getColumnValue(affiliation, 'id'), columnFilters.id) &&
            matchesColumnFilter(getColumnValue(affiliation, 'nombre'), columnFilters.nombre) &&
            matchesColumnFilter(getColumnValue(affiliation, 'tipo_operacion'), columnFilters.tipo_operacion) &&
            matchesColumnFilter(getColumnValue(affiliation, 'device_id'), columnFilters.device_id)
        );
    });

    const downloadTemplate = () => {
        const workbook = XLSX.utils.book_new();
        const templateRows = [
            {
                nombre: 'Afiliacion Centro',
                tipo_operacion: 't',
                device_id: 'DEV-1001'
            }
        ];

        const sheet = XLSX.utils.json_to_sheet(templateRows, { header: affiliationBulkColumns });
        XLSX.utils.book_append_sheet(workbook, sheet, 'Afiliaciones');
        XLSX.writeFile(workbook, 'affiliations_template.xlsx');
    };

    const normalizeRow = (row) => {
        const normalized = {};
        Object.keys(row || {}).forEach((key) => {
            const normalizedKey = key.toString().trim().toLowerCase().replace(/\s+/g, '_');
            normalized[normalizedKey] = row[key];
        });
        return normalized;
    };

    const handleBulkUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        setError('');
        setBulkResult(null);
        setBulkLoading(true);

        try {
            const arrayBuffer = await file.arrayBuffer();
            const workbook = XLSX.read(arrayBuffer, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[firstSheetName];

            if (!sheet) {
                throw new Error('No se encontró ninguna hoja en el archivo.');
            }

            const rawRows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
            const rows = rawRows.map(normalizeRow);

            if (rows.length === 0) {
                throw new Error('El archivo está vacío.');
            }

            const missingColumns = requiredAffiliationColumns.filter((column) => !Object.keys(rows[0]).includes(column));
            if (missingColumns.length > 0) {
                throw new Error(`Faltan columnas obligatorias: ${missingColumns.join(', ')}`);
            }

            const payload = [];
            const seenDeviceIDs = new Set();
            const failures = [];

            for (let index = 0; index < rows.length; index += 1) {
                const row = rows[index];
                const nombre = (row.nombre ?? '').toString().trim();
                const tipoOperacion = (row.tipo_operacion ?? '').toString().trim().toLowerCase();
                const deviceId = (row.device_id ?? '').toString().trim();

                if (!nombre || !tipoOperacion || !deviceId) {
                    failures.push({ row: index + 2, message: 'nombre, tipo_operacion y device_id son obligatorios.' });
                    continue;
                }

                if (!operationTypeLabelMap[tipoOperacion]) {
                    failures.push({ row: index + 2, message: 'tipo_operacion inválido. Use: t, r, h o a.' });
                    continue;
                }

                const normalizedDeviceID = deviceId.toLowerCase();
                if (seenDeviceIDs.has(normalizedDeviceID)) {
                    failures.push({ row: index + 2, message: 'device_id duplicado dentro del archivo.' });
                    continue;
                }

                seenDeviceIDs.add(normalizedDeviceID);
                payload.push({
                    nombre,
                    tipo_operacion: tipoOperacion,
                    device_id: deviceId
                });
            }

            if (failures.length > 0) {
                setBulkResult({
                    total: rows.length,
                    successCount: 0,
                    failureCount: failures.length,
                    failures,
                    transactional: true
                });
                throw new Error('Se encontraron errores de validación. No se insertó ninguna afiliación.');
            }

            const response = await createAffiliationsBulk(payload);
            setBulkResult({
                total: rows.length,
                successCount: response?.inserted_count || payload.length,
                failureCount: 0,
                failures: [],
                transactional: true
            });
            await fetchAffiliations();
        } catch (uploadError) {
            const detail = uploadError?.response?.data?.message || uploadError?.response?.data || uploadError?.message;
            setError(detail || 'No se pudo procesar la carga masiva de afiliaciones.');
        } finally {
            setBulkLoading(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    return (
        <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h4" component="h1">
                    Afiliaciones
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                    <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadTemplate}>
                        Descargar plantilla
                    </Button>
                    <Button
                        variant="outlined"
                        component="label"
                        startIcon={<UploadFileIcon />}
                        disabled={bulkLoading}
                    >
                        {bulkLoading ? 'Cargando...' : 'Subir Excel'}
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            hidden
                            onChange={handleBulkUpload}
                        />
                    </Button>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateModal}>
                        Agregar afiliación
                    </Button>
                    <Button variant="text" onClick={clearFilters}>Limpiar filtros</Button>
                </Box>
            </Box>

            {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

            {bulkResult && (
                <Alert severity={bulkResult.failureCount > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
                    Carga masiva transaccional. Total: {bulkResult.total}. Insertadas: {bulkResult.successCount}. Errores: {bulkResult.failureCount}.
                    {bulkResult.failureCount > 0 && (
                        <Box sx={{ mt: 1 }}>
                            {bulkResult.failures.slice(0, 5).map((failure, index) => (
                                <Typography key={`${failure.row}-${index}`} variant="body2">
                                    Fila {failure.row}: {failure.message}
                                </Typography>
                            ))}
                        </Box>
                    )}
                </Alert>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Plantilla de carga masiva: columnas requeridas nombre, tipo_operacion, device_id. La carga es transaccional (si hay errores o duplicados, no se inserta ninguna fila).
            </Typography>

            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="ID"
                                    options={affiliations.map((item) => getColumnValue(item, 'id'))}
                                    selectedOptions={columnFilters.id}
                                    onChange={(values) => updateColumnFilter('id', values)}
                                />
                            </TableCell>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="Nombre"
                                    options={affiliations.map((item) => getColumnValue(item, 'nombre'))}
                                    selectedOptions={columnFilters.nombre}
                                    onChange={(values) => updateColumnFilter('nombre', values)}
                                />
                            </TableCell>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="Tipo operación"
                                    options={affiliations.map((item) => getColumnValue(item, 'tipo_operacion'))}
                                    selectedOptions={columnFilters.tipo_operacion}
                                    onChange={(values) => updateColumnFilter('tipo_operacion', values)}
                                />
                            </TableCell>
                            <TableCell>
                                <ExcelColumnFilter
                                    label="Device ID"
                                    options={affiliations.map((item) => getColumnValue(item, 'device_id'))}
                                    selectedOptions={columnFilters.device_id}
                                    onChange={(values) => updateColumnFilter('device_id', values)}
                                />
                            </TableCell>
                            <TableCell>Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredAffiliations.map((affiliation) => (
                            <TableRow key={affiliation.id}>
                                <TableCell>{affiliation.id}</TableCell>
                                <TableCell>{affiliation.nombre}</TableCell>
                                <TableCell>{getOperationTypeLabel(affiliation.tipo_operacion)}</TableCell>
                                <TableCell>{affiliation.device_id ?? '-'}</TableCell>
                                <TableCell>
                                    <Button size="small" startIcon={<EditIcon />} onClick={() => openEditModal(affiliation)}>
                                        Editar
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Modal open={showCreateForm} onClose={closeModal}>
                <Box sx={style} component="form" onSubmit={handleCreateAffiliation}>
                    <Typography variant="h6">{editingAffiliationId ? 'Editar afiliación' : 'Crear nueva afiliación'}</Typography>
                    <TextField label="Nombre" value={affiliationForm.nombre} onChange={(event) => setAffiliationForm((prev) => ({ ...prev, nombre: event.target.value }))} fullWidth required margin="normal" />
                    <FormControl fullWidth required margin="normal">
                        <InputLabel>Tipo operación</InputLabel>
                        <Select
                            value={affiliationForm.tipo_operacion}
                            label="Tipo operación"
                            onChange={(event) => setAffiliationForm((prev) => ({ ...prev, tipo_operacion: event.target.value }))}
                        >
                            {operationTypeOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField label="Device ID" value={affiliationForm.device_id} onChange={(event) => setAffiliationForm((prev) => ({ ...prev, device_id: event.target.value }))} fullWidth required margin="normal" />

                    <Button variant="contained" type="submit" disabled={creatingAffiliation} sx={{ mt: 1 }}>
                        {creatingAffiliation ? 'Guardando...' : editingAffiliationId ? 'Guardar cambios' : 'Crear afiliación'}
                    </Button>
                </Box>
            </Modal>
        </Paper>
    );
};

export default Affiliations;